import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import { Alert, Box, CircularProgress, IconButton, Modal, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { sendSupportRequest } from "api";
import { userDataStore } from "store";
import { ToolbarButton } from "../toolbar-button";
import { getFeedbackAlertSx, submitButtonSx, supportModalStyles as s } from "./support-modal.style";
var SupportModal = function (_a) {
    var open = _a.open, onClose = _a.onClose;
    var t = useTranslation().t;
    var userEmail = userDataStore(function (state) { var _a, _b, _c; return (_c = (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.email) === null || _b === void 0 ? void 0 : _b.trim()) !== null && _c !== void 0 ? _c : ""; });
    var _b = useState(""), message = _b[0], setMessage = _b[1];
    var _c = useState(null), feedback = _c[0], setFeedback = _c[1];
    var supportMutation = useMutation({
        mutationFn: sendSupportRequest,
        onSuccess: function (data) {
            setFeedback({ tone: "success", text: data.message || t("supportModal.successMessage") });
            setMessage("");
        },
        onError: function (error) {
            var _a, _b;
            var axiosError = error;
            setFeedback({
                tone: "error",
                text: ((_b = (_a = axiosError.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || t("supportModal.errorMessage"),
            });
        },
    });
    var trimmedMessage = message.trim();
    var isSubmitting = supportMutation.isPending;
    var canSubmit = Boolean(userEmail && trimmedMessage && !isSubmitting);
    var handleChange = function (event) {
        setMessage(event.target.value);
        if (feedback)
            setFeedback(null);
    };
    var handleClose = function () {
        if (isSubmitting)
            return;
        setMessage("");
        setFeedback(null);
        onClose();
    };
    var handleSubmit = function () {
        if (!trimmedMessage) {
            setFeedback({ tone: "error", text: t("supportModal.validationNoMessage") });
            return;
        }
        if (!userEmail) {
            setFeedback({ tone: "error", text: t("supportModal.validationNoEmail") });
            return;
        }
        setFeedback(null);
        supportMutation.mutate({ email: userEmail, message: trimmedMessage });
    };
    return (_jsx(Modal, { open: open, onClose: handleClose, slotProps: {
            backdrop: {
                sx: {
                    background: "radial-gradient(circle at top, rgba(255,184,1,0.07), transparent 40%), rgba(2,4,7,0.78)",
                    backdropFilter: "blur(8px)",
                },
            },
        }, children: _jsx(Box, { sx: s.modalRoot, onClick: handleClose, children: _jsxs(Box, { sx: s.modalCard, onClick: function (e) { return e.stopPropagation(); }, children: [_jsxs(Box, { sx: s.header, children: [_jsxs(Box, { sx: s.headerTitle, children: [_jsx(Box, { sx: s.headerIconBox, children: _jsx(SupportAgentRoundedIcon, { sx: s.headerIcon }) }), _jsx(Typography, { sx: s.headerText, children: t("supportModal.title") })] }), _jsx(IconButton, { onClick: handleClose, disabled: isSubmitting, "aria-label": t("supportModal.closeAriaLabel"), sx: s.closeButton, children: _jsx(CloseRoundedIcon, { sx: { fontSize: 16 } }) })] }), _jsxs(Box, { sx: s.content, children: [_jsxs(Box, { children: [_jsx(Typography, { sx: s.sectionLabel, children: t("supportModal.replyEmail") }), _jsxs(Box, { sx: s.emailCard, children: [_jsx(Box, { sx: s.emailIconBox, children: _jsx(MarkEmailReadRoundedIcon, { sx: { fontSize: 18 } }) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: s.emailCaption, children: t("supportModal.currentAccount") }), _jsx(Typography, { sx: s.emailValue, children: userEmail || t("supportModal.emailNotAvailable") })] })] }), _jsx(Typography, { sx: s.helperText, children: t("supportModal.emailHelper") })] }), _jsxs(Box, { children: [_jsx(Typography, { sx: s.sectionLabel, children: t("supportModal.message") }), _jsx(TextField, { fullWidth: true, multiline: true, minRows: 3, maxRows: 6, value: message, onChange: handleChange, placeholder: t("supportModal.messagePlaceholder"), sx: s.textField })] }), feedback && (_jsx(Alert, { severity: feedback.tone, sx: getFeedbackAlertSx(feedback.tone), children: feedback.text })), _jsxs(Box, { sx: s.actionRow, children: [_jsx(ToolbarButton, { onClick: handleClose, disabled: isSubmitting, ariaLabel: t("supportModal.closeAriaLabel"), sx: s.cancelBtnSx, children: t("supportModal.cancel") }), _jsxs(ToolbarButton, { onClick: handleSubmit, disabled: !canSubmit, active: true, ariaLabel: t("supportModal.sendAriaLabel"), sx: submitButtonSx, children: [isSubmitting ? _jsx(CircularProgress, { size: 14, sx: { color: "#120e06", mr: "6px" } }) : null, isSubmitting ? t("supportModal.sending") : t("supportModal.send")] })] })] })] }) }) }));
};
export default SupportModal;
